import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import FlipCard from '../FlipCard/FlipCard';
import ListingFront from './ListingFront';
import ListingBack from './ListingBack';
import ExternalListingFront from './ExternalListingFront';
import ExternalListingBack from './ExternalListingBack';
import { TooltipContext } from './TooltipContext';
import logo from '../../assets/images/logo.png';
import './FlippableListingCard.css';
import ShareButton from '../ShareLink/ShareButton';
import ListingDetailsModal from './ListingDetailsModal';
import { AuthContext } from '../../contexts/AuthContext';
import { listingReviewViewer, rememberListingBack } from '../../utils/listingReviewHistory';



// onEditRow(key) turns this card into the employers listing BUILDER: both faces
// become tap-to-edit and forward their row key to the host's fact sheets. It is
// the listing counterpart of the seam CRNAProfileCard already exposes for the
// anonymous /providers profile card, so the two builders are one experience.
// Absent on every viewer surface, where nothing about this file changes.
// ownerMint says the signed-in caller owns this listing, which routes the share
// through the authed endpoint for a permanent, reusable link instead of the
// public endpoint's new seven-day row per press. Off by default, because every
// other host of this card shares as a visitor with no token to mint with.
// hideEmptyRows is the same prop CRNAProfileCard already threads into
// ProfileFront/ProfileBack, threaded here into both listing faces: a row the
// employer never filled in is omitted rather than drawn as a dash. Default
// FALSE, so every surface that does not ask for it renders as before.
const FlippableListingCard = forwardRef(({ profile = {}, isCertified = false, onFlip, hideShareButton = false, showFlipButton = false, alignmentData = null, viewerType = 'provider', onEditRow = null, hideSourceBadge = false, ownerMint = false, shareResourceId = null, hideEmptyRows = false, onOpenDetails = null }, ref) => {
  const authContext = React.useContext(AuthContext);
  const viewer = listingReviewViewer(authContext?.auth);
  const [openId, setOpenId] = React.useState(null);
  const [showDetails, setShowDetails] = React.useState(false);
  const flipCardRef = useRef(null);
  const openDetails = React.useCallback(() => {
    if (typeof onOpenDetails === 'function') {
      onOpenDetails(profile);
      return;
    }
    setShowDetails(true);
  }, [onOpenDetails, profile]);
  const closeDetails = React.useCallback(() => setShowDetails(false), []);
  const handleFlip = React.useCallback((flipped) => {
    if (flipped && viewerType === 'provider' && !onEditRow && !ownerMint) {
      rememberListingBack(profile, viewer);
    }
    onFlip?.(flipped);
  }, [profile, viewer, viewerType, onEditRow, ownerMint, onFlip]);

  // Expose flip/reset to parent via ref
  useImperativeHandle(ref, () => ({
    flip: () => flipCardRef.current?.flip(),
    reset: () => flipCardRef.current?.reset(),
  }));

  // Determine listing source type (internal = RxRooster, gaswork = external)
  const sourceType = profile?.sourceType || profile?._listingType || profile?.source || 'internal';

  // Check if this is an external listing (Any source that is not explicit internal is external)
  const isExternalListing = sourceType !== 'internal';

  // Check if the card has meaningful back content that requires flipping
  const hasClinicalContent = () => {
    // External listings always have full description on back - always flippable
    if (isExternalListing) {
      return true;
    }

    const clinicalCaseTypes = profile.clinicalExpectations?.requiredCaseTypes?.length > 0;
    // Check both the direct clinicalExpectations path and the new facilityDetails path
    const hasEmr = (profile.clinicalExpectations?.emrSystem &&
      profile.clinicalExpectations.emrSystem !== '–') ||
      (profile.facilityDetails?.clinicalExpectations?.emrSystem &&
        profile.facilityDetails.clinicalExpectations.emrSystem !== '–') ||
      (profile.clinical?.emrSystem && profile.clinical.emrSystem !== '–');
    const hasWebsite = profile.website && profile.website !== '–';
    const hasDescription = !!(
      profile.fullDescription ||
      profile.description ||
      profile.briefDescription ||
      profile.summary
    );
    const hasSourceLink = !!(
      profile.sourceUrl ||
      profile.originalListingUrl ||
      profile.originalUrl ||
      profile.listingUrl ||
      profile.url ||
      profile.applyUrl ||
      profile.inquireLink
    );
    const includesHousing = profile.includesHousing || profile.compensation?.includesHousing;
    const includesTravel = profile.includesTravel || profile.compensation?.includesTravel;
    const malpracticeCovered = profile.malpracticeCovered || profile.compensation?.malpracticeCovered;
    const hasPerks = includesHousing || includesTravel || malpracticeCovered;
    const hasCareTeamModel = profile.clinical?.careTeamModel?.length > 0;

    // Only make flippable if there's actually content on the back worth showing
    return clinicalCaseTypes || hasEmr || hasWebsite || hasDescription || hasSourceLink || hasPerks || hasCareTeamModel;
  };

  // A card being BUILT is always flippable, whatever is on the back yet: the
  // back face is where the case types, skills, care model, EMR and perks are
  // added, so gating the flip on those already being filled in would make the
  // empty rows that ask for them unreachable.
  const needsFlipping = onEditRow ? true : hasClinicalContent();

  // Safe facility type extraction
  const getFacilityType = () => {
    return profile?.facilityType || 'Facility';
  };

  // The logo's INTRINSIC size, not a display size: the file is 286x74, and CSS
  // is what caps it (max-width 64px, height auto, so ~17px tall).
  //
  // It used to be declared 64x100, which told the browser to reserve a 0.64
  // aspect ratio box for a 3.86 image. Between mount and decode the footer was
  // therefore 125px instead of 42px. On a fixed-height card nothing showed for
  // it. On the employers BUILDER card, which is as tall as its content, it made
  // the card jump ~83px on every flip: FlipCard rebuilds its footer on each
  // render, so the image is briefly un-decoded every single time.
  const footerLogo = (
    <img src={logo} alt="RxRooster Logo" width={286} height={74} loading="lazy" decoding="async" />
  );

  // Share button for footer
  const shareButtonElement = !hideShareButton ? (
    <ShareButton
      resourceType="groupListing"
      resourceId={shareResourceId || profile?._id || profile?.id}
      resourceTitle={`${getFacilityType()} Listing`}
      variant="outline-primary"
      size="sm"
      autoGenerate={!profile?.shareUrl}
      shareUrl={profile?.shareUrl}
      shareToken={profile?.shareToken}
      ownerMint={ownerMint}
      className="shadow-sm bg-white"
    >
      Share Job
    </ShareButton>
  ) : null;

  return (
    <TooltipContext.Provider value={{ openId, setOpenId }}>
      <FlipCard
        key={profile._id || profile.id}
        ref={flipCardRef}
        flippable={needsFlipping}
        showFlipButton={showFlipButton && needsFlipping}
        front={
          isExternalListing ? (
            <ExternalListingFront
              listing={profile}
              sourceType={sourceType}
              alignmentData={alignmentData}
              viewerType={viewerType}
              onOpenDetails={openDetails}
            />
          ) : (
            <ListingFront
              profile={profile}
              isCertified={isCertified}
              showClinicalInfo={!needsFlipping}
              sourceType={sourceType}
              alignmentData={alignmentData}
              viewerType={viewerType}
              onEditRow={onEditRow}
              // Only the internal front is threaded: an EXTERNAL card is a row
              // we ingested rather than one being built, so it always has a
              // source worth naming and never reaches this prop.
              hideSourceBadge={hideSourceBadge}
              // Likewise internal-only: the external faces are separate
              // components with their own row model and are not this change's
              // to alter.
              hideEmptyRows={hideEmptyRows}
            />
          )
        }
        back={
          isExternalListing ? (
            <ExternalListingBack listing={profile} onOpenDetails={openDetails} />
          ) : (
            <ListingBack
              profile={profile}
              isCertified={isCertified}
              sourceType={sourceType}
              onEditRow={onEditRow}
              hideEmptyRows={hideEmptyRows}
              onOpenDetails={openDetails}
            />
          )
        }
        logo={footerLogo}
        shareButton={shareButtonElement}
        onFlip={handleFlip}
      />
      {!onOpenDetails && (
        <ListingDetailsModal
          listing={profile}
          open={showDetails}
          onClose={closeDetails}
        />
      )}
    </TooltipContext.Provider>
  );
});

export default FlippableListingCard;
